import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  /** 0-indexed current page. */
  page: number;
  /** Total page count (from server). */
  totalPages: number;
  /** Total item count (rendered in the "X results" tag). */
  totalItems: number;
  /** Items per page — only used to render the "X of Y" range hint. */
  size: number;
  onChange: (page: number) => void;
}

/**
 * Numbered pagination footer:
 *
 *   ‹  1  2  3 … 12 13 14  ›
 *
 * Active page rendered in the sunset gradient. Ellipses appear when the
 * total page count exceeds the window so we never render more than ~7
 * numbered buttons regardless of the dataset size.
 */
export function Pagination({ page, totalPages, totalItems, size, onChange }: Props) {
  if (totalPages <= 1) {
    return (
      <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-foreground">
        <span>{totalItems} {totalItems === 1 ? "result" : "results"}</span>
      </div>
    );
  }

  const buttons = buildPaginationWindow(page, totalPages);

  const start = page * size + 1;
  const end = Math.min((page + 1) * size, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border">
      <span className="text-xs text-muted-foreground">
        Showing <span className="font-semibold text-foreground">{start}–{end}</span> of {totalItems}
      </span>

      <nav aria-label="Pagination" className="flex items-center gap-1">
        <NavButton
          ariaLabel="Previous page"
          disabled={page === 0}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </NavButton>

        {buttons.map((b, i) =>
          b === "ellipsis" ? (
            <span key={`gap-${i}`} className="px-1.5 text-muted-foreground">
              <MoreHorizontal className="size-4" />
            </span>
          ) : (
            <button
              key={b}
              type="button"
              onClick={() => onChange(b)}
              aria-current={b === page ? "page" : undefined}
              className={cn(
                "min-w-8 h-8 rounded-md px-2 text-xs font-semibold transition cursor-pointer",
                b === page
                  ? "bg-gradient-sunset text-primary-foreground shadow-glow"
                  : "border border-border bg-card text-foreground hover:bg-muted",
              )}
            >
              {b + 1}
            </button>
          ),
        )}

        <NavButton
          ariaLabel="Next page"
          disabled={page >= totalPages - 1}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight className="size-4" />
        </NavButton>
      </nav>
    </div>
  );
}

function NavButton({
  children, disabled, onClick, ariaLabel,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "flex size-8 items-center justify-center rounded-md border border-border bg-card text-foreground transition",
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Build a windowed list of page numbers + ellipsis tokens for the
 * pagination strip. Always shows first + last and a small range around
 * the current page, so very large datasets don't produce hundreds of
 * buttons.
 */
function buildPaginationWindow(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const result: (number | "ellipsis")[] = [0];
  const startMid = Math.max(1, current - 1);
  const endMid = Math.min(total - 2, current + 1);
  if (startMid > 1) result.push("ellipsis");
  for (let i = startMid; i <= endMid; i++) result.push(i);
  if (endMid < total - 2) result.push("ellipsis");
  result.push(total - 1);
  return result;
}
